import type { BoardItem, BoardResult, BoardTally } from "./api/types";

/*
 * The 话术榜's arithmetic, shared by its two pages: the board, which ranks
 * what was sent by what came of it, and the 销售工作流, which puts the
 * winners in order for 小爱 (components/ScriptBoardView.tsx, WorkflowEditor.tsx).
 */

export type Verdict = "win" | "lose" | "few" | "unclear";

/*
 * A row the board likes is an idea, not proof: customers who kept talking bought more whatever they were sent, and a
 * colleague sends the price sheet to the one asking the price. So a sure row reads 值得试 / 少用 — what to try as a B —
 * and only a trial says 赢 (owner, 2026-11-02: 「是用来做展示让人看…有数据依据的不是凭感觉？」).
 */

export const VERDICT: Record<Verdict, [string, string]> = {
  win: ["good", "值得试"],
  lose: ["bad", "少用"],
  few: ["", "人少，先别信"],
  unclear: ["", "还看不出"],
};

export function saved<T>(key: string, ok: (v: unknown) => v is T, fallback: T): T {
  try {
    const v = JSON.parse(window.localStorage.getItem(key) ?? "null") as unknown;
    if (ok(v)) return v;
  } catch {
    /* a private window starts on the default */
  }
  return fallback;
}

export function keep(key: string, v: unknown) {
  try {
    window.localStorage.setItem(key, JSON.stringify(v));
  } catch {
    /* kept for this visit only */
  }
}

type Loose<T> = { [K in keyof T]?: T[K] | null };
const list = <T>(x: T[] | null | undefined): T[] => (Array.isArray(x) ? x : []);
const tally = (t: BoardTally | null | undefined): BoardTally => ({ customers: t?.customers ?? 0, bought: t?.bought ?? 0 });

/**
 * The board as the pages read it. The API's Go writes an empty list as null (a nil slice): staging on 10/25 answered
 * a month with no route at all as "routes": null, and `routes.filter` took the whole 话术榜 down (QA Q-01). Every
 * list here is a list, every tally a tally, whatever came.
 */
export function boardOf(b: Loose<BoardResult> | null | undefined): BoardResult {
  const stages: Record<string, BoardItem[]> = {};
  for (const [k, v] of Object.entries(b?.stages ?? {})) stages[k] = list(v);
  return {
    customers: b?.customers ?? 0,
    bought: b?.bought ?? 0,
    no_price: tally(b?.no_price),
    price: tally(b?.price),
    stages,
    routes: list(b?.routes).map((r) => ({ ...r, steps: list(r.steps) })),
    cases: list(b?.cases).map((c) => ({ ...c, says: list(c.says), points: list(c.points), ways: list(c.ways), ...(c.avoid ? { avoid: list(c.avoid) } : {}) })),
    ...(b?.takeover ? { takeover: { ...b.takeover, at: list(b.takeover.at), items: list(b.takeover.items) } } : {}),
    ...(b?.reach ? { reach: Object.fromEntries(Object.entries(b.reach).map(([k, t]) => [k, tally(t)])) } : {}),
  };
}

export const md = (d: string) => {
  const [, m, dd] = d.split("-").map(Number);
  return m && dd ? `${m}/${dd}` : d;
};

export const rate = (bought: number, of: number) => (of > 0 ? (100 * bought) / of : 0);

/** Where a rate of k in n probably lies (Wilson, 95%), in percent. */
export function wilson(n: number, k: number): [number, number] {
  if (n <= 0) return [0, 100];
  const z = 1.96;
  const p = k / n;
  const d = 1 + (z * z) / n;
  const c = p + (z * z) / (2 * n);
  const m = z * Math.sqrt((p * (1 - p)) / n + (z * z) / (4 * n * n));
  return [Math.max(0, (100 * (c - m)) / d), Math.min(100, (100 * (c + m)) / d)];
}

/**
 * Fewer customers than this and a row is 人少，先别信, however it went: one buyer of one was 赢 beside two of whom one
 * bought (人少), and 教小爱 quoted it (QA V-25, 10/27). A single customer's range clears a low average all the same.
 * Five until 11/2, when 值得试 listed rows of seven and twelve customers, sure by the arithmetic among some eighty rows
 * — the few that clear a 95% range by chance alone (owner, 11/2 evening: 「做展示用你就这样展示？」).
 */
export const SURE_MIN = 30;

/** 赢 / 输 only when the whole likely range is above / below the average, and enough customers were in it. */
export function verdict(n: number, k: number, avg: number): Verdict {
  if (n < SURE_MIN) return "few";
  const [lo, hi] = wilson(n, k);
  if (lo > avg) return "win";
  if (hi < avg) return "lose";
  return hi - lo > 24 ? "few" : "unclear";
}

/** Whether two rates differ more than chance would (two-proportion z ≥ 1.96). */
export function differs(n1: number, k1: number, n2: number, k2: number): boolean {
  if (n1 <= 0 || n2 <= 0) return false;
  const p1 = k1 / n1;
  const p2 = k2 / n2;
  const p = (k1 + k2) / (n1 + n2);
  const se = Math.sqrt(p * (1 - p) * (1 / n1 + 1 / n2));
  return se > 0 && Math.abs(p1 - p2) / se >= 1.96;
}

/** A customer asked for a name and address was buying already: such a step
 *  says nothing about what sells, and is kept out of every average. */
export const closing = (it: BoardItem) => it.label.startsWith("收单") || it.role === "pay";

export interface Row {
  it: BoardItem;
  rate: number;
  lift: number;
  v: Verdict;
}

/** A row named only as 「something else」: nobody could do it again, so it is no row (it still counts in the average). */
export const vague = (it: BoardItem) => it.label === "其他录音" || it.label === "录音（还没有文字）" || it.label === "聊别的（打字）";

/** A row the board is sure of: enough customers, and clearly above or below its step's average. */
export const known = (r: Row) => !closing(r.it) && (r.v === "win" || r.v === "lose");

/**
 * A step's rows, each against the step's own average: of its customers (reach, the API's count of who was sent anything
 * there, each once), when the API gives it. Summed over the rows instead, a customer sent three things counted three
 * times, and the page showed two averages for one step — the road's stop and the cards' 平均 (owner, 11/2 night:
 * 「检查清楚数据展示得是否准确？」).
 */
export function rowsOf(items: BoardItem[], reach?: BoardTally): { avg: number; rows: Row[] } {
  const counted = items.filter((x) => !closing(x) && x.customers > 0);
  const avg =
    reach && reach.customers > 0
      ? rate(reach.bought, reach.customers)
      : rate(
          counted.reduce((a, x) => a + x.bought, 0),
          counted.reduce((a, x) => a + x.customers, 0),
        );
  const rows = items
    .filter((x) => x.customers > 0 && !vague(x))
    .map((it) => {
      const r = rate(it.bought, it.customers);
      return { it, rate: r, lift: r - avg, v: closing(it) ? ("unclear" as Verdict) : verdict(it.customers, it.bought, avg) };
    })
    .sort((a, b) => Number(closing(a.it)) - Number(closing(b.it)) || b.lift - a.lift || b.it.customers - a.it.customers);
  return { avg, rows };
}

export const signed = (n: number) => {
  const r = Math.round(n);
  return `${r > 0 ? "+" : ""}${r}`;
};
